import { PageController } from '@client/components/_core/layout/PageController';
import { VAsicProductSelect } from '@client/components/_shared/forms/view-form/VAsicProductSelect';
import { VDatePicker } from '@client/components/_shared/forms/view-form/VDatePicker';
import { VDropdown } from '@client/components/_shared/forms/view-form/VDropdown';
import { VEntityOwnerSelect } from '@client/components/_shared/forms/view-form/VEntityOwnerSelect';
import { VReadOnly } from '@client/components/_shared/forms/view-form/VReadOnly';
import { VSwitch } from '@client/components/_shared/forms/view-form/VSwitch';
import { VTextArea } from '@client/components/_shared/forms/view-form/VTextArea';
import { VTextInput } from '@client/components/_shared/forms/view-form/VTextInput';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { FieldConfigurationViewRenderer } from '@client/components/_ui/data-entry/FieldConfigurationRenderer';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import { VAsicOutcomeSelect } from '@client/components/complaints/view-form/VAsicOutcomeSelect';
import { VComplaintBrandSelect } from '@client/components/complaints/view-form/VComplaintBrandSelect';
import { VComplaintIssueSelect } from '@client/components/complaints/view-form/VComplaintIssueSelect';
import { VDateReceivedEdit } from '@client/components/complaints/view-form/VDateReceivedEdit';
import { VPostcodeAutoComplete } from '@client/components/complaints/view-form/VPostcodeAutoComplete';
import { VPreferredChannelSelect } from '@client/components/complaints/view-form/VPreferredChannelSelect';
import { useConfigServiceLoader } from '@client/hooks/business/configuration/useConfigServiceLoader';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useUser } from '@client/hooks/business/user/useUser';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useDeferredSectionsReady } from '@client/hooks/system/useDeferredSectionsReady';
import { ICONS } from '@client/styles/theme';
import { requiredFieldRule } from '@client/utils';
import { ErrorBoundary } from '@sentry/react';
import {
    AGES,
    CHANNELS,
    COMPLAINANT_SOLUTIONS,
    COMPLAINANT_TYPE,
    COMPLAINANT_TYPES,
    COMPLAINT_INCIDENT_DESCRIPTION_MAX_LENGTH,
    GENDERS,
    RELATED_TO_AFS_LICENSEE,
    RELATIONSHIPS,
} from '@shared/constants';
import { hasPermission } from '@shared/permissions/roles';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Card, Col, Row } from 'antd';
import { lazy, Suspense, useEffect } from 'react';
import { useCanEditComplaint, useCanLinkComplaintIncidents } from '../hooks';
import { complaintFormStore } from '../store';

const DeferredManageTimeline = lazy(() =>
    import('@client/components/_ui/display/ManageTimeline').then((module) => ({ default: module.ManageTimeline })),
);
const DeferredIncidentList = lazy(() =>
    import('@client/components/incidents/IncidentList').then((module) => ({ default: module.IncidentList })),
);
const DeferredTaskList = lazy(() =>
    import('@client/components/tasks/TaskList').then((module) => ({ default: module.TaskList })),
);
const DeferredNotesCard = lazy(() =>
    import('@client/components/notes/NotesCard').then((module) => ({ default: module.NotesCard })),
);

export const Route = createFileRoute('/console/$orgId/compliance/complaints/details/$id/manage')({
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ComplaintDetailsManage />
        </PageController>
    );
}

function ComplaintDetailsManage() {
    const deferredSectionsReady = useDeferredSectionsReady();
    const orgId = useOrgId();
    const navigate = useNavigate();
    const complaint = complaintFormStore.use.formData();
    const canEdit = useCanEditComplaint();
    // CUR-296: linking incidents is permitted even when the complaint is closed (no reopen needed).
    const canLinkIncidents = useCanLinkComplaintIncidents();
    const setFieldForEditing = complaintFormStore.use.setFieldForEditing();
    const user = useUser();
    // CUR-367: the "exclude from ASIC report" toggle is org-admin only. It stays editable regardless of
    // the complaint's open/closed status — the ASIC IDR report covers closed complaints, so excluding a
    // closed one is the common case. The server (updateComplaint route) re-checks the org-admin role.
    const canManageAsicExclusion = hasPermission(user.roles, 'org:admin');

    const { data: brand } = useConfigServiceLoader((configService) => configService.fetchComplaintBrandConfiguration());

    useEffect(() => {
        if (!complaint.complainantGender && complaint.complainantType === COMPLAINANT_TYPE.INDIVIDUAL) {
            message.info('Please select gender.');
            setFieldForEditing('complainantGender');
        }
    }, [complaint.complainantType]);

    const disabled = !canEdit;

    const VEntityOwnerSelectInstance = VEntityOwnerSelect({
        entity: 'complaint',
        formStore: complaintFormStore,
        isReadOnly: disabled,
    });

    const VPreferredChannelSelectInstance = VPreferredChannelSelect({
        formStore: complaintFormStore,
        isReadOnly: disabled,
    });

    const VAsicOutcomeSelectInstance = VAsicOutcomeSelect({
        formStore: complaintFormStore,
        isReadOnly: disabled,
    });

    return (
        <>
            <Outlet />
            <Space orientation="vertical" size={16} className="w-full">
                <Row gutter={[16, 16]}>
                    <Col span={12}>
                        <Card
                            title={
                                <div className="flex items-center gap-2">
                                    <ICONS.complainant className="card-title-icon" />
                                    <span>Complainant</span>
                                </div>
                            }
                            size="small"
                            className="h-full"
                        >
                            <Row gutter={[16, 16]}>
                                <Col span={8}>
                                    <VTextInput
                                        label="First name"
                                        field="complainantFirstName"
                                        isReadOnly={disabled}
                                        formStore={complaintFormStore}
                                        rules={requiredFieldRule}
                                    />
                                </Col>
                                <Col span={8}>
                                    <VTextInput
                                        label="Surname"
                                        field="complainantLastName"
                                        isReadOnly={disabled}
                                        formStore={complaintFormStore}
                                        rules={requiredFieldRule}
                                    />
                                </Col>
                                <Col span={8}>
                                    <VDropdown
                                        label="Relationship"
                                        field="relationship"
                                        formStore={complaintFormStore}
                                        rules={requiredFieldRule}
                                        options={RELATIONSHIPS.map((value) => ({ value }))}
                                        isReadOnly={disabled}
                                        enableOtherOption
                                    />
                                </Col>

                                <Col span={8}>
                                    <VDropdown
                                        label="Type"
                                        field="complainantType"
                                        formStore={complaintFormStore}
                                        rules={requiredFieldRule}
                                        options={COMPLAINANT_TYPES.map(({ label }) => ({
                                            value: label,
                                        }))}
                                        isReadOnly={disabled}
                                    />
                                </Col>
                                <Col span={8}>
                                    <VSwitch
                                        label="Referred by AFCA"
                                        field="referredByAfca"
                                        formStore={complaintFormStore}
                                        isReadOnly={disabled}
                                    />
                                </Col>
                                {complaint.referredByAfca && (
                                    <>
                                        <Col span={8}>
                                            <VDatePicker
                                                label="Date referred (AFCA)"
                                                field="referredByAfcaDate"
                                                formStore={complaintFormStore}
                                                isReadOnly={disabled}
                                                rules={requiredFieldRule}
                                                dateOnly
                                            />
                                        </Col>

                                        <Col span={8}>
                                            <VTextInput
                                                label="Reference# (AFCA)"
                                                field="afcaReferenceNo"
                                                formStore={complaintFormStore}
                                                isReadOnly={disabled}
                                            />
                                        </Col>
                                    </>
                                )}
                                {/* Gender/Age are hidden when type !== "Individual or couple". Values
                                    are intentionally retained in the DB so toggling the type back
                                    restores them. The server update path accepts this; create paths
                                    still enforce the inverse rule. */}
                                {complaint.complainantType === COMPLAINANT_TYPE.INDIVIDUAL && (
                                    <>
                                        <Col span={8}>
                                            <VDropdown
                                                label="Gender"
                                                field="complainantGender"
                                                formStore={complaintFormStore}
                                                options={GENDERS.map(({ label }) => ({
                                                    value: label,
                                                }))}
                                                isReadOnly={disabled}
                                                rules={requiredFieldRule}
                                            />
                                        </Col>
                                        <Col span={8}>
                                            <VDropdown
                                                label="Age"
                                                field="complainantAge"
                                                formStore={complaintFormStore}
                                                options={AGES.map(({ label }) => ({ value: label }))}
                                                isReadOnly={disabled}
                                                rules={requiredFieldRule}
                                            />
                                        </Col>
                                    </>
                                )}

                                <Col span={8}>
                                    <VPostcodeAutoComplete formStore={complaintFormStore} isReadOnly={disabled} />
                                </Col>
                                <Col span={8}>
                                    <VDropdown
                                        label="Channel"
                                        field="channel"
                                        formStore={complaintFormStore}
                                        options={CHANNELS.map(({ label }) => ({
                                            value: label,
                                        }))}
                                        rules={requiredFieldRule}
                                        isReadOnly={disabled}
                                    />
                                </Col>
                                <Col span={8}>
                                    <VPreferredChannelSelectInstance.Channel />
                                </Col>
                                <Col span={8}>
                                    <VPreferredChannelSelectInstance.ChannelValue />
                                </Col>
                                <FieldConfigurationViewRenderer
                                    entity="COMPLAINT"
                                    section="complainant"
                                    fieldPathPrefix="customFieldsData"
                                    formStore={complaintFormStore}
                                    isReadOnly={disabled}
                                    layout={{
                                        colSpan: 8,
                                    }}
                                />
                            </Row>
                        </Card>
                    </Col>

                    <Col span={12}>
                        <Card
                            title={
                                <div className="flex items-center gap-2">
                                    <ICONS.handlingInfo className="card-title-icon" />
                                    <span>Handling info</span>
                                </div>
                            }
                            size="small"
                            className="h-full"
                        >
                            <Row gutter={[16, 16]}>
                                <Col span={8}>
                                    <VReadOnly label="Complaint#" field="id" formStore={complaintFormStore} />
                                </Col>
                                <Col span={8}>
                                    <VDateReceivedEdit formStore={complaintFormStore} isReadOnly={disabled} />
                                </Col>

                                <Col span={8}>
                                    <VEntityOwnerSelectInstance.HandlingParty />
                                </Col>

                                <Col span={8}>
                                    <VEntityOwnerSelectInstance.Owner />
                                </Col>
                                <Col span={8}>
                                    <VAsicProductSelect
                                        formStore={complaintFormStore}
                                        isReadOnly={disabled}
                                        rules={requiredFieldRule}
                                    />
                                </Col>
                                <Col span={8}>
                                    <VTextInput
                                        label="Policy#"
                                        field="policyNo"
                                        formStore={complaintFormStore}
                                        isReadOnly={disabled}
                                    />
                                </Col>

                                <Col span={8}>
                                    <VReadOnly label="Status" field="status" formStore={complaintFormStore} />
                                </Col>
                                <Col span={8}>
                                    <VTextInput
                                        label="Claim#"
                                        field="externalClaimId"
                                        formStore={complaintFormStore}
                                        isReadOnly={disabled}
                                    />
                                </Col>
                                <Col span={8}>
                                    <VDropdown
                                        label="Is the complaint about the authorized representative of an AFS licensee?"
                                        field="afsLicenseeRelated"
                                        formStore={complaintFormStore}
                                        isReadOnly={disabled}
                                        options={RELATED_TO_AFS_LICENSEE.map(({ label }) => ({
                                            value: label,
                                        }))}
                                    />
                                </Col>

                                {(brand?.isDisplayedOnExternalForm || brand?.isDisplayedOnInternalForm) && (
                                    <Col span={8}>
                                        <VComplaintBrandSelect formStore={complaintFormStore} isReadOnly={disabled} />
                                    </Col>
                                )}
                                {canManageAsicExclusion && (
                                    <Col span={8}>
                                        <VSwitch
                                            label="Exclude from ASIC report"
                                            field="excludeFromAsicReport"
                                            formStore={complaintFormStore}
                                        />
                                    </Col>
                                )}
                                <FieldConfigurationViewRenderer
                                    entity="COMPLAINT"
                                    section="handling_info"
                                    fieldPathPrefix="customFieldsData"
                                    formStore={complaintFormStore}
                                    isReadOnly={disabled}
                                    layout={{
                                        colSpan: 8,
                                    }}
                                />
                            </Row>
                        </Card>
                    </Col>

                    <Col span={12}>
                        <Card
                            title={
                                <div className="flex items-center gap-2">
                                    <ICONS.complaint className="card-title-icon" />
                                    <span>Nature of complaint</span>
                                </div>
                            }
                            size="small"
                            className="h-full"
                        >
                            <Row gutter={[16, 16]}>
                                <Col span={24}>
                                    <VComplaintIssueSelect
                                        formStore={complaintFormStore}
                                        isReadOnly={disabled}
                                        rules={requiredFieldRule}
                                    />
                                </Col>

                                <Col span={24}>
                                    <VTextArea
                                        label="Incident description"
                                        field="incidentDescription"
                                        formStore={complaintFormStore}
                                        isReadOnly={disabled}
                                        maxLength={COMPLAINT_INCIDENT_DESCRIPTION_MAX_LENGTH}
                                    />
                                </Col>

                                <Col span={24}>
                                    <VDropdown
                                        label="Solution the complainant is seeking"
                                        field="complainantSolution"
                                        formStore={complaintFormStore}
                                        options={COMPLAINANT_SOLUTIONS.map((value) => ({
                                            value,
                                        }))}
                                        isReadOnly={disabled}
                                        enableOtherOption
                                    />
                                </Col>

                                <FieldConfigurationViewRenderer
                                    entity="COMPLAINT"
                                    section="nature_of_complaint"
                                    fieldPathPrefix="customFieldsData"
                                    formStore={complaintFormStore}
                                    isReadOnly={disabled}
                                    layout={{
                                        colSpan: 24,
                                    }}
                                />
                            </Row>
                        </Card>
                    </Col>
                    <Col span={12}>
                        <Card
                            title={
                                <div className="flex items-center gap-2">
                                    <ICONS.outcome className="card-title-icon" />
                                    <span>Outcome details</span>
                                </div>
                            }
                            size="small"
                            className="h-full"
                        >
                            <Row gutter={[16, 16]}>
                                <Col span={24}>
                                    <VAsicOutcomeSelectInstance.Outcomes />
                                </Col>

                                {VAsicOutcomeSelectInstance.isMonetaryRemedyFieldVisible && (
                                    <Col span={24}>
                                        <VAsicOutcomeSelectInstance.MonetaryRemedy />
                                    </Col>
                                )}

                                <Col span={24}>
                                    <VTextArea
                                        label="Outcome details"
                                        field="outcomeDetails"
                                        formStore={complaintFormStore}
                                        isReadOnly={disabled}
                                    />
                                </Col>
                                <Col span={24}>
                                    <VSwitch
                                        label="Was the complaint solved in favour of the customer?"
                                        field="wasSolvedInFavourOfCustomer"
                                        formStore={complaintFormStore}
                                        isReadOnly={disabled}
                                    />
                                </Col>

                                <FieldConfigurationViewRenderer
                                    entity="COMPLAINT"
                                    section="outcome_details"
                                    fieldPathPrefix="customFieldsData"
                                    formStore={complaintFormStore}
                                    isReadOnly={disabled}
                                    layout={{
                                        colSpan: 24,
                                    }}
                                />
                            </Row>
                        </Card>
                    </Col>
                    <Col span={24} className="min-h-[216px]">
                        <ErrorBoundary
                            fallback={
                                <Card size="small" title="Complaint timeline">
                                    <p role="alert">This panel could not be loaded. Reload the page to try again.</p>
                                </Card>
                            }
                        >
                            <Suspense
                                fallback={
                                    <Card loading size="small" title="Complaint timeline" className="h-[216px]" />
                                }
                            >
                                {deferredSectionsReady ? (
                                    <DeferredManageTimeline
                                        entity="complaint"
                                        entityId={complaint.id}
                                        disabled={disabled}
                                    />
                                ) : (
                                    <Card loading size="small" title="Complaint timeline" className="h-[216px]" />
                                )}
                            </Suspense>
                        </ErrorBoundary>
                    </Col>

                    <Col span={24} className="min-h-[216px]">
                        <ErrorBoundary
                            fallback={
                                <Card size="small" title="Incidents">
                                    <p role="alert">This panel could not be loaded. Reload the page to try again.</p>
                                </Card>
                            }
                        >
                            <Suspense fallback={<Card loading size="small" title="Incidents" className="h-[216px]" />}>
                                {deferredSectionsReady ? (
                                    <DeferredIncidentList
                                        entity="complaint"
                                        entityId={complaint.id}
                                        isReadOnly={disabled}
                                        rightAddon={
                                            canLinkIncidents && (
                                                <Button.Add.Link
                                                    to="/console/$orgId/compliance/complaints/details/$id/manage/incidents"
                                                    params={{
                                                        orgId,
                                                        id: complaint.id,
                                                    }}
                                                    variant="tertiary"
                                                    label="Add incident"
                                                />
                                            )
                                        }
                                    />
                                ) : (
                                    <Card loading size="small" title="Incidents" className="h-[216px]" />
                                )}
                            </Suspense>
                        </ErrorBoundary>
                    </Col>

                    <Col xxl={12} md={24} className="min-h-[216px]">
                        <ErrorBoundary
                            fallback={
                                <Card size="small" title="Tasks">
                                    <p role="alert">This panel could not be loaded. Reload the page to try again.</p>
                                </Card>
                            }
                        >
                            <Suspense fallback={<Card loading size="small" title="Tasks" className="h-[216px]" />}>
                                {deferredSectionsReady ? (
                                    <DeferredTaskList
                                        size="small"
                                        relatedEntity="complaint"
                                        relatedEntityId={complaint.id}
                                        hideAddTask={disabled}
                                        handlingPartyIds={complaint.handlingPartyIds}
                                    />
                                ) : (
                                    <Card loading size="small" title="Tasks" className="h-[216px]" />
                                )}
                            </Suspense>
                        </ErrorBoundary>
                    </Col>
                    <Col xxl={12} md={24} className="min-h-[216px]">
                        <ErrorBoundary
                            fallback={
                                <Card size="small" title="Notes">
                                    <p role="alert">This panel could not be loaded. Reload the page to try again.</p>
                                </Card>
                            }
                        >
                            <Suspense fallback={<Card loading size="small" title="Notes" className="h-[216px]" />}>
                                {deferredSectionsReady ? (
                                    <DeferredNotesCard
                                        entity="complaint"
                                        entityId={complaint.id}
                                        disabled={disabled}
                                        onAddNote={() =>
                                            navigate({
                                                to: '/console/$orgId/compliance/complaints/details/$id/manage/note/add',
                                                params: {
                                                    orgId,
                                                    id: complaint.id,
                                                },
                                            })
                                        }
                                    />
                                ) : (
                                    <Card loading size="small" title="Notes" className="h-[216px]" />
                                )}
                            </Suspense>
                        </ErrorBoundary>
                    </Col>
                </Row>
            </Space>
        </>
    );
}
